import moment from 'moment';

import { TranslateService } from '@ngx-translate/core';

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output
} from '@angular/core';

import { JobStatusBookingPipe } from '../transaction-center/jobs/transaction-center.jobs.status-booking.pipe';

import { BookingDiaryEventModel }    from './typings/booking-diary.event';
import { BookingDiaryGroupModel }    from './typings/booking-diary.mechanic';
import { BookingDiaryListMode }      from './typings/booking-diary.view';
import { BookingDiaryMonthDayModel } from './typings/booking-diary.month';

import {
  BookingDiaryListActionModel,
  BookingDiaryListActionType,
  BookingDiaryListRowModel
} from './typings/booking-diary.list';

import {
  BookingDiaryAllBookingModel,
  BookingDiaryUnassignedJobModel
} from './typings/booking-diary.backlog';

import {
  DATE_FORMAT,
  formatClockLabel
} from './booking-diary.layout';

import { vehicleLabel } from './booking-diary.options';

import { statusCodeFor } from '../transaction-center/jobs/transaction-center.jobs.status-tone';

// how close to the bottom, as a share of the visible height, counts as reaching the end
const SCROLL_END_SHARE : number = 0.2;

// The diary read as a table: the period's bookings, a row per day, the backlog, or every booking
@Component({
  selector   : 'wor-booking-diary-list',
  template: require('./booking-diary.list.component.html')
})
export class BookingDiaryListComponent implements OnChanges {
  @Input() bookings     : Array<BookingDiaryAllBookingModel>    = [];
  @Input() days         : Array<BookingDiaryMonthDayModel>      = [];
  @Input() emptyMessage : string                                = '';
  @Input() events       : Array<BookingDiaryEventModel>         = [];
  @Input() groups       : Array<BookingDiaryGroupModel>         = [];
  @Input() hasMore      : boolean                               = false;
  @Input() isBusy       : boolean                               = false;
  @Input() isLoading    : boolean                               = false;
  @Input() jobs         : Array<BookingDiaryUnassignedJobModel> = [];
  @Input() mode         : BookingDiaryListMode                  = 'events';
  @Input() page         : number                                = 1;
  @Input() pageSize     : number                                = 0;
  @Input() total        : number                                = 0;

  @Output() bookingOpened : EventEmitter<BookingDiaryAllBookingModel>    = new EventEmitter<BookingDiaryAllBookingModel>();
  @Output() dayOpened     : EventEmitter<string>                         = new EventEmitter<string>();
  @Output() eventOpened   : EventEmitter<BookingDiaryEventModel>         = new EventEmitter<BookingDiaryEventModel>();
  @Output() jobOpened     : EventEmitter<BookingDiaryUnassignedJobModel> = new EventEmitter<BookingDiaryUnassignedJobModel>();
  @Output() pageChange    : EventEmitter<number>                         = new EventEmitter<number>();
  @Output() reachedEnd    : EventEmitter<void>                           = new EventEmitter<void>();
  @Output() rowAction     : EventEmitter<BookingDiaryListActionModel>    = new EventEmitter<BookingDiaryListActionModel>();

  private mechanicNames : { [id : string] : string }   = {};
  private statusLabels  : { [code : string] : string } = {};

  constructor (
    private jobStatusBookingPipe : JobStatusBookingPipe,
    private translateService     : TranslateService
  ) {}

  ngOnChanges () : void {
    this.mechanicNames = {};

    ( this.groups || [] ).forEach(group => group.mechanics.forEach(mechanic => {
      this.mechanicNames[mechanic.id] = mechanic.name;
    }));
  }

  // an action sits inside the clickable row, so it must act rather than also open the row
  act ( clickEvent : MouseEvent, type : BookingDiaryListActionType, row : BookingDiaryListRowModel ) : void {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();

    this.rowAction.emit({ ...row, type });
  }

  bookingDate ( booking : BookingDiaryAllBookingModel ) : string {
    const date = String(booking.event_date || '').slice(0, 10);

    return date
      ? moment(date, DATE_FORMAT).format('ddd D MMM YYYY')
      : '';
  }

  // a booking is open or closed; its job status belongs to the appointments
  bookingStatus ( code : string ) : string {
    return code === 'C'
      ? this.translateService.instant('STATUS_TYPES.CLOSED')
      : this.translateService.instant('STATUS_TYPES.OPEN');
  }

  dayLabel ( day : BookingDiaryMonthDayModel ) : string {
    return moment(day.date, DATE_FORMAT).format('ddd D MMM');
  }

  dayStatus ( day : BookingDiaryMonthDayModel ) : string {
    if ( !day.jobs ) {
      return '';
    }

    return day.availableHours <= 0
      ? this.translateService.instant('BOOKING_SPACE.DIARY.FULLY_BOOKED')
      : this.translateService.instant('BOOKING_SPACE.DIARY.HOURS_AVAILABLE', { hours: day.availableHours });
  }

  eventDate ( event : BookingDiaryEventModel ) : string {
    return moment(event.date, DATE_FORMAT).format('ddd D MMM');
  }

  get isEmpty () : boolean {
    if ( this.isLoading ) {
      return false;
    }

    switch ( this.mode ) {
      case 'all':
        return !this.bookings.length;
      case 'days':
        return !this.days.length;
      case 'unassigned':
        return !this.jobs.length;
      default:
        return !this.events.length;
    }
  }

  jobDate ( job : BookingDiaryUnassignedJobModel ) : string {
    const date = String(job.event_date || '').slice(0, 10);

    return date
      ? moment(date, DATE_FORMAT).format('ddd D MMM')
      : '';
  }

  jobDescription ( job : BookingDiaryUnassignedJobModel ) : string {
    return [ job.event_description, job.event_item_description ]
      .filter(part => !!part)
      .join(' - ');
  }

  mechanicName ( mechanicId : string ) : string {
    return this.mechanicNames[mechanicId] || '';
  }

  onScroll ( scrollEvent : Event ) : void {
    const element = scrollEvent.target as HTMLElement;
    const left    = element.scrollHeight - element.scrollTop - element.clientHeight;

    if ( left <= element.clientHeight * SCROLL_END_SHARE ) {
      this.reachedEnd.emit();
    }
  }

  // asked for every row on every pass, so each code is translated once and kept
  statusLabel ( code : string ) : string {
    if ( this.statusLabels[code] === undefined ) {
      this.statusLabels[code] = this.jobStatusBookingPipe.transform(statusCodeFor(code)) || '';
    }

    return this.statusLabels[code];
  }

  timeLabel ( minutes : number ) : string {
    return formatClockLabel(minutes);
  }

  trackBooking ( index : number, booking : BookingDiaryAllBookingModel ) : string {
    return booking.id;
  }

  trackDay ( index : number, day : BookingDiaryMonthDayModel ) : string {
    return day.date;
  }

  trackEvent ( index : number, event : BookingDiaryEventModel ) : string {
    return event.id;
  }

  trackJob ( index : number, job : BookingDiaryUnassignedJobModel ) : string {
    return job.id;
  }

  vehicleLabel ( booking : BookingDiaryAllBookingModel ) : string {
    return vehicleLabel(booking);
  }
}
